// ---------------------------------------------------------------------------
// Runtime configuration loader
// ---------------------------------------------------------------------------
// In production the SPA fetches /amplify_outputs.json once at startup; the
// file is written into the bucket by the CDK BucketDeployment in
// infra/survey-ui-stack.ts and served by CloudFront with no-store caching.
// Shape matches AWS Amplify Gen 2's ResourcesConfig (auth.*) so the same
// artifact can be passed to Amplify.configure() once the auth wiring lands
// (A2 of track-adf-plan). Non-Amplify fields (apiUrl, env) live under
// `custom`; Amplify ignores anything outside its own schema.
//
// In dev (`npm run ui:dev`) the loader is a no-op and getApiBaseUrl()
// returns '/api', preserving the Vite proxy in ui/vite.config.ts.

export interface AmplifyOutputs {
  version: string;
  auth: {
    aws_region: string;
    user_pool_id: string;
    user_pool_client_id: string;
    [key: string]: unknown;
  };
  custom?: {
    apiUrl?: string;
    env?: string;
  };
}

let loadedConfig: AmplifyOutputs | null = null;
let loadPromise: Promise<AmplifyOutputs | null> | null = null;

/**
 * Fetches /amplify_outputs.json once and caches it. Idempotent — subsequent
 * calls return the same promise. Awaited in ui/src/main.tsx before the
 * React tree renders, so by the time any component runs, config is
 * already in memory and getApiBaseUrl() is a synchronous lookup.
 */
export function loadConfig(): Promise<AmplifyOutputs | null> {
  if (loadPromise) return loadPromise;

  if (import.meta.env.DEV) {
    loadedConfig = null;
    loadPromise = Promise.resolve(null);
    return loadPromise;
  }

  loadPromise = fetch('/amplify_outputs.json', { cache: 'no-store' }).then(
    async (res) => {
      if (!res.ok) {
        throw new Error(
          `Failed to load /amplify_outputs.json: HTTP ${res.status}`,
        );
      }
      const json = (await res.json()) as Partial<AmplifyOutputs>;
      if (
        !json.auth ||
        typeof json.auth.aws_region !== 'string' ||
        typeof json.auth.user_pool_id !== 'string' ||
        typeof json.auth.user_pool_client_id !== 'string'
      ) {
        throw new Error('Invalid /amplify_outputs.json shape: missing auth fields');
      }
      if (typeof json.custom?.apiUrl !== 'string') {
        throw new Error('Invalid /amplify_outputs.json shape: missing custom.apiUrl');
      }
      loadedConfig = json as AmplifyOutputs;
      return loadedConfig;
    },
  );

  return loadPromise;
}

/**
 * Returns the API base URL. Must be called at request time, not captured
 * at module load — module-level constants can evaluate before
 * loadConfig() resolves and lock in a stale fallback.
 *
 * The `/public` suffix is the public-gateway prefix introduced by the
 * dual-gateway pattern (infra adoption in PRs #47/#58): the same REST API
 * exposes browser-facing routes under `/public/*` and service-to-service
 * routes under `/internal/*`. Callers pass paths like `/templates`;
 * concatenation produces `<host>/<stage>/public/templates`. Trailing
 * slash on apiUrl is stripped first so the result has exactly one slash
 * between the stage and `public`.
 */
export function getApiBaseUrl(): string {
  if (import.meta.env.DEV) return '/api';
  if (!loadedConfig) {
    throw new Error(
      'getApiBaseUrl() called before loadConfig() resolved. Ensure loadConfig() is awaited in main.tsx before rendering.',
    );
  }
  // Validated non-null in loadConfig.
  return loadedConfig.custom!.apiUrl!.replace(/\/$/, '') + '/public';
}

/**
 * Returns the shared-services API base URL. Derived from the browser
 * hostname (survey.dev.amchealthtest.com → api.dev.amchealthtest.com).
 * Paths like `/shared/public/org/nodes` are appended by callers.
 */
export function getSharedApiBaseUrl(): string {
  if (import.meta.env.DEV) return '/api';
  const host = window.location.hostname;
  const apiHost = host.replace(/^survey\./, 'api.');
  return `https://${apiHost}`;
}
